import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart3,
  Navigation,
  Clock,
  StickyNote,
  Bookmark,
  Video,
  Brain,
  GraduationCap,
  FileText,
  Activity,
  TrendingUp,
  Flame,
  Award,
  Lightbulb,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dashboardImage from "@/assets/dashboard-showcase.png";
import { FeatureDetailDialog } from "@/components/landing/FeatureDetailDialog";
import { FeatureMiniMockup, type MiniMockupVariant } from "@/components/landing/FeatureMiniMockup";
import { useFeatureHoverPopup } from "@/components/landing/useFeatureHoverPopup";

type DashboardFeatureKey =
  | "progress"
  | "navigation"
  | "timeTracking"
  | "notes"
  | "bookmarks"
  | "videoFeatures"
  | "flashcards"
  | "quizzes"
  | "resources"
  | "studyAnalytics"
  | "detailedAnalytics"
  | "streaks"
  | "certificates"
  | "insights";

interface FeatureItem {
  key: DashboardFeatureKey;
  icon: typeof BarChart3;
  category: "dashboard" | "learning" | "study" | "progress";
  /** Mini-mockup variant to render in the popup hero band when no real screenshot exists. */
  mockup: MiniMockupVariant;
  /** Optional CTA route — only set when there's a meaningful in-app destination. */
  ctaHref?: string;
}

const features: FeatureItem[] = [
  { key: "progress", icon: BarChart3, category: "dashboard", mockup: "progress", ctaHref: "/my-analytics" },
  { key: "navigation", icon: Navigation, category: "dashboard", mockup: "navigation", ctaHref: "/dashboard" },
  { key: "timeTracking", icon: Clock, category: "dashboard", mockup: "timeTracking", ctaHref: "/my-analytics" },
  { key: "notes", icon: StickyNote, category: "learning", mockup: "notes", ctaHref: "/dashboard" },
  { key: "bookmarks", icon: Bookmark, category: "learning", mockup: "bookmarks", ctaHref: "/dashboard" },
  { key: "videoFeatures", icon: Video, category: "learning", mockup: "videoFeatures", ctaHref: "/dashboard" },
  { key: "flashcards", icon: Brain, category: "study", mockup: "flashcards", ctaHref: "/dashboard" },
  { key: "quizzes", icon: GraduationCap, category: "study", mockup: "quizzes", ctaHref: "/dashboard" },
  { key: "resources", icon: FileText, category: "study", mockup: "resources", ctaHref: "/dashboard" },
  { key: "studyAnalytics", icon: Activity, category: "study", mockup: "studyAnalytics", ctaHref: "/my-analytics" },
  { key: "detailedAnalytics", icon: TrendingUp, category: "progress", mockup: "detailedAnalytics", ctaHref: "/my-analytics" },
  { key: "streaks", icon: Flame, category: "progress", mockup: "streaks", ctaHref: "/gamification" },
  { key: "certificates", icon: Award, category: "progress", mockup: "certificates", ctaHref: "/gamification" },
  { key: "insights", icon: Lightbulb, category: "progress", mockup: "insights", ctaHref: "/my-analytics" },
];

const categoryGradients = {
  dashboard: "from-primary/20 to-accent/20",
  learning: "from-accent/20 to-secondary/20",
  study: "from-secondary/20 to-primary/20",
  progress: "from-primary/20 to-accent/20",
} as const;

export const LearningDashboardShowcase = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { open, setOpen, activeKey, handleSelect } =
    useFeatureHoverPopup<DashboardFeatureKey>();

  const active = features.find((f) => f.key === activeKey) ?? null;
  const activeBullets = active
    ? (t(`featureDetails.dashboard.${active.key}.bullets`, { returnObjects: true }) as unknown)
    : [];
  const activeBulletsArray = Array.isArray(activeBullets) ? (activeBullets as string[]) : [];

  return (
    <section className="py-12 lg:py-16 bg-gradient-subtle relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10" />
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[120px] -z-10" />

      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 space-y-3 max-w-4xl mx-auto"
        >
          <Badge variant="secondary" className="mb-3 px-3 py-1">
            {t("dashboardShowcase.badge")}
          </Badge>
          <h2 className="heading-hero mb-3 bg-gradient-primary bg-clip-text text-transparent">
            {t("dashboardShowcase.title")}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto">
            {t("dashboardShowcase.subtitle")}
          </p>
        </motion.div>

        {/* Dashboard Screenshot - Full Width on Top */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-10 relative max-w-5xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-glass border glass-border bg-card/30 backdrop-blur-xl hover:shadow-glass-hover transition-shadow duration-500">
            <img
              src={dashboardImage}
              alt={t("dashboardShowcase.imageAlt")}
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none" />
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="space-y-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="text-center space-y-2"
          >
            <h3 className="text-xl md:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              {t("dashboardShowcase.gridTitle", "Experiência completa de aprendizagem")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
              {t("dashboardShowcase.gridSubtitle", "Clique em qualquer card para ver detalhes")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4 lg:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const gradientClass = categoryGradients[feature.category];
              const isActive = open && activeKey === feature.key;

              return (
                <motion.div
                  key={feature.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.8 + index * 0.05 }}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(feature.key)}
                    aria-label={`${t(`dashboardShowcase.features.${feature.key}.title`)} — ${t("common.viewDetails", "Ver detalhes")}`}
                    className="w-full h-full text-left"
                  >
                    <Card
                      className={`relative h-full min-h-[280px] overflow-hidden bg-card/30 backdrop-blur-xl glass-border transition-all duration-300 group ${
                        isActive
                          ? "border-primary/50 shadow-glass-hover"
                          : "hover:border-primary/30 hover:shadow-glass-hover hover:scale-[1.02]"
                      }`}
                    >
                      {/* Mockup preview band */}
                      <div
                        className={`relative h-24 w-full overflow-hidden border-b glass-border bg-gradient-to-br ${gradientClass}`}
                      >
                        <FeatureMiniMockup variant={feature.mockup} size="inline" />
                      </div>
                      <div
                        className={`absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-background/70 backdrop-blur-md border border-primary/30 flex items-center justify-center transition-opacity ${
                          isActive
                            ? "opacity-100 bg-primary/20"
                            : "opacity-80 group-hover:opacity-100 group-hover:bg-primary/20"
                        }`}
                      >
                        <Plus className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <CardContent className="p-4 space-y-3">
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradientClass} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                        >
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors pr-8">
                            {t(`dashboardShowcase.features.${feature.key}.title`)}
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {t(`dashboardShowcase.features.${feature.key}.description`)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 1.2 }}
          className="text-center"
        >
          <Button
            size="default"
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 text-sm shadow-glass hover:shadow-glass-hover hover:scale-105 transition-all duration-300"
          >
            {t("dashboardShowcase.cta")}
          </Button>
        </motion.div>
      </div>

      {active && (
        <FeatureDetailDialog
          open={open}
          onOpenChange={setOpen}
          icon={active.icon}
          title={t(`dashboardShowcase.features.${active.key}.title`)}
          shortDescription={t(`dashboardShowcase.features.${active.key}.description`)}
          longDescription={t(`featureDetails.dashboard.${active.key}.long`)}
          bullets={activeBulletsArray}
          bulletsTitle={t("featureDetails.whatYouCanDo")}
          customHero={<FeatureMiniMockup variant={active.mockup} />}
          ctaLabel={active.ctaHref ? t("featureDetails.tryItNow") : undefined}
          ctaHref={active.ctaHref}
          gradient={categoryGradients[active.category]}
        />
      )}
    </section>
  );
};
